import { useEffect, useState } from 'react';

// Year-navigation state for the Gantt timeline (STD-014 split out of
// SimpleGanttChart). Owns the selected year, prev/next handlers, the
// "can't go past the current calendar year" boundary, and keeps the selection
// valid when the set of years-with-data changes (e.g. after maintenance records
// load). Notifies the parent on every year change.
export interface UseYearNavigation {
  currentYear: number;
  handlePreviousYear: () => void;
  handleNextYear: () => void;
  // True when the next-year button should be disabled (at/after the current
  // calendar year).
  isAtLatestYear: boolean;
}

export function useYearNavigation(
  allYears: number[],
  onYearChange?: (year: number) => void
): UseYearNavigation {
  // Default to the latest year that has data, falling back to this year.
  const [currentYear, setCurrentYear] = useState<number>(() => {
    return allYears[0] || new Date().getFullYear();
  });

  // If the available years change and the current selection is no longer valid,
  // snap to the latest year with data.
  useEffect(() => {
    if (allYears.length > 0 && !allYears.includes(currentYear)) {
      setCurrentYear(allYears[0]);
    }
  }, [allYears, currentYear]);

  // Notify parent when year changes.
  useEffect(() => {
    onYearChange?.(currentYear);
  }, [currentYear, onYearChange]);

  const handlePreviousYear = () => {
    setCurrentYear((prev) => prev - 1);
  };

  const handleNextYear = () => {
    setCurrentYear((prev) => prev + 1);
  };

  return {
    currentYear,
    handlePreviousYear,
    handleNextYear,
    isAtLatestYear: currentYear >= new Date().getFullYear(),
  };
}
